"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Inline glossary for the brain-region terms in the memory series. The post
 * body marks a term as an ordinary markdown link — `[hippocampus](#glossary-hippocampus)`
 * — which survives DOMPurify untouched. On mount this client component finds
 * those links inside the article and turns them into tap-to-open definition
 * popovers: anchored under the word on desktop, a bottom sheet on touch. This
 * is why the blog can stay markdown — inline interactivity is layered on after
 * render rather than needing components embedded mid-paragraph.
 */

interface TermDef {
  title: string;
  role: string;
  body: string;
}

const GLOSSARY_PREFIX = "#glossary-";

const TERMS: Record<string, TermDef> = {
  hippocampus: {
    title: "Hippocampus",
    role: "Encode",
    body: "In the brain, it binds the moment-to-moment stream into episodes you can later recall. Here, it reads a window of conversation and emits atomic, act-tagged memories.",
  },
  amygdala: {
    title: "Amygdala",
    role: "Weigh",
    body: "In the brain, it flags what matters emotionally, so charged moments are stored more strongly. Here, it scores each episode's salience at the moment of capture.",
  },
  neocortex: {
    title: "Neocortex",
    role: "Consolidate",
    body: "In the brain, it is where memories are slowly integrated into lasting knowledge. Here, a nightly pass groups episodes and abstracts them into durable facts.",
  },
  recall: {
    title: "Recall",
    role: "Resurface",
    body: "Bringing the right memory back at the right moment. Here, it blends relevance, salience, and forgetting to surface what a given turn needs, and never rewrites what it reads.",
  },
};

interface OpenState {
  key: string;
  anchor: HTMLAnchorElement;
  rect: DOMRect;
  mobile: boolean;
}

export function BrainGlossary() {
  const [open, setOpen] = useState<OpenState | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Turn the markdown glossary links into interactive terms once the article
  // is in the DOM. Listeners are attached imperatively because the prose is
  // rendered via dangerouslySetInnerHTML — React never owns these nodes.
  useEffect(() => {
    const root: ParentNode = document.querySelector("[data-blog-article]") ?? document;
    const links = Array.from(
      root.querySelectorAll<HTMLAnchorElement>(`a[href*="${GLOSSARY_PREFIX}"]`),
    );
    const cleanups = links.map((a) => {
      const href = a.getAttribute("href") ?? "";
      const key = href.slice(href.indexOf(GLOSSARY_PREFIX) + GLOSSARY_PREFIX.length);
      if (!TERMS[key]) return () => undefined;

      a.classList.add("glossary-term");
      a.setAttribute("role", "button");
      a.setAttribute("aria-haspopup", "dialog");

      const toggle = () => {
        const rect = a.getBoundingClientRect();
        const mobile = !window.matchMedia("(min-width: 640px)").matches;
        setOpen((cur) => (cur?.anchor === a ? null : { key, anchor: a, rect, mobile }));
      };
      const onClick = (e: MouseEvent) => {
        e.preventDefault();
        toggle();
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggle();
        }
      };
      a.addEventListener("click", onClick);
      a.addEventListener("keydown", onKey);
      return () => {
        a.removeEventListener("click", onClick);
        a.removeEventListener("keydown", onKey);
      };
    });
    return () => cleanups.forEach((c) => c());
  }, []);

  // Dismiss on outside interaction, Escape, or any scroll/resize — an anchored
  // card would otherwise drift away from its term.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (cardRef.current?.contains(t)) return;
      if (open.anchor.contains(t)) return; // the term's own toggle handles this
      setOpen(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        open.anchor.focus();
        setOpen(null);
      }
    };
    const onReflow = () => setOpen(null);
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [open]);

  // Place the desktop card under its term, clamped to the viewport, flipping
  // above when there is not enough room below. Positioning is applied straight
  // to the node (not via state) — the card renders at opacity 0 and this reveal
  // avoids a first paint at the wrong spot without a cascading re-render.
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!open || open.mobile || !card) return;
    const box = card.getBoundingClientRect();
    const pad = 12;
    const left = Math.min(Math.max(pad, open.rect.left), window.innerWidth - box.width - pad);
    const below = open.rect.bottom + 8;
    const top =
      below + box.height > window.innerHeight - pad
        ? Math.max(pad, open.rect.top - box.height - 8)
        : below;
    card.style.left = `${left.toString()}px`;
    card.style.top = `${top.toString()}px`;
    card.style.opacity = "1";
  }, [open]);

  // Move focus into the card so keyboard and screen-reader users land on it.
  useEffect(() => {
    if (open) cardRef.current?.focus();
  }, [open]);

  if (!open) return null;
  const def = TERMS[open.key];

  const close = () => {
    open.anchor.focus();
    setOpen(null);
  };

  return (
    <>
      {open.mobile && (
        <div className="fixed inset-0 z-[60] bg-ink/25 backdrop-blur-[1px]" aria-hidden="true" />
      )}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal={open.mobile}
        aria-label={def.title}
        aria-describedby="glossary-desc"
        tabIndex={-1}
        className={
          open.mobile
            ? "fixed inset-x-3 bottom-3 z-[61] rounded-xl border border-border-subtle bg-surface-elevated p-4 shadow-xl outline-none"
            : "fixed z-[61] w-[19rem] max-w-[calc(100vw-1.5rem)] rounded-xl border border-border-subtle bg-surface-elevated p-4 shadow-xl outline-none transition-opacity duration-100"
        }
        style={open.mobile ? undefined : { opacity: 0 }}
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-signal-deep">
            {def.title}
            <span className="text-foreground-faint"> · {def.role}</span>
          </span>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="-mr-1 -mt-1 shrink-0 rounded px-1.5 text-lg leading-none text-foreground-faint transition hover:text-foreground"
          >
            ×
          </button>
        </div>
        <p id="glossary-desc" className="mt-2 text-sm leading-relaxed text-foreground-muted">
          {def.body}
        </p>
      </div>
    </>
  );
}
